import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import MLTooltip from '../../../../MLTooltip/MLTooltip';
import MLIcon from '../../../../MLIcon/MLIcon';
import UngradedIcon from '../../../../UngradedIcon/UngradedIcon';
import {
  formatGradePercent,
  formatGradePoints,
  hasDueDateException,
  GRADEBOOK_SETTINGS,
} from './utils';
import { GRADING_TYPE } from '../../../../../utils/constants';
import { getGradeDisplaySpanAriaLabel } from './gradeDisplayAriaLabel';
import styles from './EditableGradeCell.module.css';

/**
 * EditableGradeCell component for displaying and editing grades
 * Handles click events to open the grade override dialog
 * Uses shared formatting utilities for consistent grade display
 */
export function EditableGradeCell({
  grade,
  showPercent,
  roundPercentType,
  settings,
  isBold,
  studentId,
  studentName,
  assignmentId,
  assignmentName,
  onGradeClick,
  isEditable,
  rowIndex,
  columnIndex,
}) {
  // Check if this is an ungraded assignment
  const isUngraded = grade?.grading_type === GRADING_TYPE.UNGRADED;

  // Format the grade using shared utilities
  let formattedGrade;
  if (isUngraded) {
    // For ungraded assignments, render the UngradedIcon component
    formattedGrade = (
      <UngradedIcon
        status={grade?.status}
        studentGrade={grade?.studentGrade}
        tool={grade?.tool}
        subtype={grade?.subtype}
      />
    );
  } else if (showPercent) {
    formattedGrade = formatGradePercent(
      grade?.studentGrade,
      grade,
      settings,
      roundPercentType,
    );
  } else {
    formattedGrade = formatGradePoints(grade, settings);
  }

  const handleClick = useCallback(() => {
    if (!isEditable || !onGradeClick) return;

    // Pass all required fields matching V1's handleEditGradeClick
    onGradeClick({
      userId: studentId,
      userName: studentName,
      assignmentId,
      assignmentName,
      studentPoints: grade?.studentPoints,
      studentGrade: grade?.studentGrade,
      assignedPoints: grade?.assigned_points,
      subtype: grade?.subtype,
      tool: grade?.tool,
      dueDate: grade?.studentAssignmentDueAt,
      gradeComment: grade?.gradeComment || '',
      dateCompleted: grade?.dateCompleted,
      isAssigned: grade?.isAssigned,
      isInstructorEntered: grade?.isInstructorEntered,
      grading_type: grade?.grading_type,
      status: grade?.status,
      isOverdue: grade?.isOverdue,
      // Additional fields for generating links in the parent
      hasLaunched: grade?.hasLaunched,
      course_id: grade?.course_id,
    });
  }, [
    isEditable,
    onGradeClick,
    studentId,
    studentName,
    assignmentId,
    assignmentName,
    grade,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const isLate = grade?.receivedLatePenalty;
  const isOverdue = grade?.isOverdue;
  const isAssigned = grade?.isAssigned;
  const isDropped = grade?.isDropped;

  const containerClasses = [
    styles.container,
    isBold ? styles.bold : styles.normal,
    isDropped ? styles.droppedState : '',
  ]
    .filter(Boolean)
    .join(' ');

  const buttonClasses = [
    styles.editableButton,
    isBold ? styles.bold : styles.normal,
    isDropped ? styles.droppedState : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasException = hasDueDateException(grade);

  // Hidden from the accessibility tree so it doesn't register as a second
  // accessible object alongside the grade button/span in the same cell
  const exceptionIcon = hasException ? (
    <MLTooltip content="Due date exception provided">
      <div
        className={styles.dueDateIconContainer}
        tabIndex={-1}
        aria-hidden="true"
      >
        <MLIcon
          className={styles.infoIcon}
          type="info_outline"
          role="presentation"
        />
      </div>
    </MLTooltip>
  ) : null;

  const gradeSpanAriaLabel = isEditable
    ? getGradeDisplaySpanAriaLabel(formattedGrade)
    : isAssigned === false
      ? 'Not assigned to student'
      : null;

  const gradeContent = (
    <div className={styles.gradeContent}>
      <div className={styles.gradeLine}>
        {hasException && (
          <span className={styles.srOnly}>Due date exception provided, </span>
        )}
        {isLate && !isOverdue && <span className={styles.lateLabel}>Late</span>}
        {isOverdue && <span className={styles.lateLabel}>Overdue</span>}
        <span aria-label={gradeSpanAriaLabel}>{formattedGrade}</span>
      </div>
      {isDropped && <i className={styles.droppedLabel}>Dropped</i>}
    </div>
  );

  if (!isEditable) {
    return (
      <div className={containerClasses}>
        {exceptionIcon}
        {gradeContent}
      </div>
    );
  }

  // Build data-grid-id for editable button (row{n}-col{n} format for testing)
  const dataGridId =
    rowIndex !== undefined && columnIndex !== undefined
      ? `row${rowIndex}-col${columnIndex}`
      : undefined;

  return (
    <div className={styles.cellWrapper}>
      {exceptionIcon}
      <button
        type="button"
        className={buttonClasses}
        tabIndex={-1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-grid-id={dataGridId}
      >
        {gradeContent}
        {/* aria-label on the button would squash the actual cell content */}
        <span className={styles.srOnly}>, Edit grade</span>
      </button>
    </div>
  );
}

EditableGradeCell.propTypes = {
  grade: PropTypes.shape({
    studentGrade: PropTypes.number,
    studentPoints: PropTypes.number,
    assigned_points: PropTypes.number,
    isDropped: PropTypes.bool,
    isLate: PropTypes.bool,
    isOverdue: PropTypes.bool,
    isAssigned: PropTypes.bool,
    status: PropTypes.string,
    tool: PropTypes.string,
    subtype: PropTypes.string,
    grading_type: PropTypes.number,
    studentId: PropTypes.string,
    exceptions: PropTypes.arrayOf(
      PropTypes.shape({
        exception_type: PropTypes.string,
        user_ids: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }),
  showPercent: PropTypes.bool,
  roundPercentType: PropTypes.number,
  settings: PropTypes.array,
  isBold: PropTypes.bool,
  studentId: PropTypes.string,
  studentName: PropTypes.string,
  assignmentId: PropTypes.string,
  assignmentName: PropTypes.string,
  onGradeClick: PropTypes.func,
  isEditable: PropTypes.bool,
  rowIndex: PropTypes.number,
  columnIndex: PropTypes.number,
};

EditableGradeCell.defaultProps = {
  grade: null,
  showPercent: true,
  roundPercentType: GRADEBOOK_SETTINGS.roundPercentWhole,
  settings: [],
  isBold: false,
  studentId: '',
  studentName: '',
  assignmentId: '',
  assignmentName: '',
  onGradeClick: null,
  isEditable: false,
  rowIndex: undefined,
  columnIndex: undefined,
};

export default EditableGradeCell;
